/**
 * ai.ts — the typed contract between the CMS Worker and the separate AI
 * Worker (apps/ai). Both sides import these Zod schemas, so a change here is
 * a compile error on whichever side forgot to follow.
 *
 * Design rules (see the v2 plan, "Two planes"):
 *  - The AI Worker is STATELESS and holds NO CMS, media or Git credentials.
 *    It only holds LLM-provider keys. The CMS sends everything the model may
 *    use (brief, approved memory pack, sources) in the request, and gets a
 *    PROPOSAL back. The CMS stores the proposal and applies it only after a
 *    human approves it — the AI Worker has no write path to the CMS at all.
 *  - Every request names the provider/model explicitly (the CMS resolves the
 *    site's setting), so switching provider is a setting change, not a deploy.
 *  - Every response is validated against these schemas on BOTH sides; a
 *    response that fails validation is rejected, never "repaired" silently.
 */
import { z } from "zod";
import { brandProfileSchema, KNOWLEDGE_LIMITS, knowledgeItemSchema, knowledgeRefSchema } from "./brain.ts";
import { allowedNumbers, evidenceItemSchema, extractAllNumbers, extractNumbers, findUnsupportedNumbers, isFigure, stripNonProse, type EvidenceItem, type UnsupportedNumber } from "./evidence.ts";

/** Providers the AI Worker has an adapter for. `anthropic` is the default. */
export const AI_PROVIDERS = ["anthropic", "openai", "google", "xai", "meta"] as const;
export const aiProviderSchema = z.enum(AI_PROVIDERS);
export type AiProvider = z.infer<typeof aiProviderSchema>;

export const DEFAULT_AI_PROVIDER: AiProvider = "anthropic";

/** Which provider+model to use for one call. `model` omitted = the AI
 * Worker's configured default for that provider (env var), so model names
 * live in config rather than being hard-coded in two places. */
export const aiModelChoiceSchema = z.object({
  provider: aiProviderSchema.default(DEFAULT_AI_PROVIDER),
  model: z.string().min(1).max(100).optional(),
});
export type AiModelChoice = z.infer<typeof aiModelChoiceSchema>;

/** Approved brand memory the CMS injects into a call. Only rules a manager
 * approved ever appear here (the AI Worker cannot add or approve rules). */
export const memoryPackSchema = z.object({
  /** Manager-approved style/behaviour rules, e.g. "Never use the word 'leverage'". */
  rules: z.array(z.object({ id: z.string(), text: z.string().max(500) })).max(50).default([]),
  /** Short excerpts of the closest approved articles, as style examples. */
  examples: z
    .array(z.object({ id: z.string(), title: z.string(), excerpt: z.string().max(3000) }))
    .max(5)
    .default([]),
  /** Topic preferences derived from past approve/reject decisions. */
  preferredTopics: z.array(z.string().max(200)).max(30).default([]),
  avoidedTopics: z.array(z.string().max(200)).max(30).default([]),
  /** The manager-written brand profile (voice, audience, what to avoid). */
  profile: brandProfileSchema.default({ voice: "", audience: "", avoid: "" }),
  /** Passages from the knowledge library most relevant to this task, cited K1, K2… */
  knowledge: z.array(knowledgeItemSchema).max(KNOWLEDGE_LIMITS.maxPassagesInPrompt).default([]),
});
export type MemoryPack = z.infer<typeof memoryPackSchema>;

/** A reference to an evidence item in the request ("E1"…"E40"). The only handle the model may cite. */
export const evidenceRefSchema = z.string().regex(/^E\d{1,3}$/);

/** A source the model may cite. The CMS (or the human) supplies these; the
 * draft step must not invent URLs. */
export const aiSourceSchema = z.object({
  url: z.string().url(),
  title: z.string().max(300),
  /** The exact text excerpt the claim may be checked against. */
  excerpt: z.string().max(6000),
});
export type AiSource = z.infer<typeof aiSourceSchema>;

// ---------------------------------------------------------------------------
// POST /v1/ideas — suggest topics (nothing is saved; output is a proposal)
// ---------------------------------------------------------------------------

/** Signals the CMS passes in (from Search Console / GA4 / Semrush when those
 * connectors exist, or typed in by a human). Each is optional. */
export const ideaSignalSchema = z.object({
  query: z.string().max(300),
  source: z.enum(["manual", "search_console", "ga4", "semrush", "existing_content", "evidence"]).default("manual"),
  /** Set when the signal was built from a stored evidence record. */
  evidenceRef: evidenceRefSchema.optional(),
  impressions: z.number().int().nonnegative().optional(),
  clicks: z.number().int().nonnegative().optional(),
  volume: z.number().int().nonnegative().optional(),
});
export type IdeaSignal = z.infer<typeof ideaSignalSchema>;

export const ideasRequestSchema = z.object({
  model: aiModelChoiceSchema.default({ provider: DEFAULT_AI_PROVIDER }),
  /** The seed keyword or topic the human typed (ATP-style "start from a keyword"). */
  seed: z.string().min(2).max(300),
  audience: z.string().max(300).optional(),
  signals: z.array(ideaSignalSchema).max(100).default([]),
  /** Stored evidence the model may cite (measured, estimated, observed, reported, classified). Empty = nothing numeric may be claimed. */
  evidence: z.array(evidenceItemSchema).max(40).default([]),
  /** Titles/URLs that already exist, so the model can avoid duplicates. */
  existingTitles: z.array(z.string().max(300)).max(500).default([]),
  memory: memoryPackSchema.default({ rules: [], examples: [], preferredTopics: [], avoidedTopics: [], profile: { voice: "", audience: "", avoid: "" }, knowledge: [] }),
  count: z.number().int().min(1).max(20).default(10),
});
export type IdeasRequest = z.infer<typeof ideasRequestSchema>;

/** Question groups like AnswerThePublic: how/why/what/vs/for. */
export const QUESTION_GROUPS = ["how", "what", "why", "when", "who", "which", "vs", "for", "other"] as const;
export const questionGroupSchema = z.enum(QUESTION_GROUPS);

export const topicIdeaSchema = z.object({
  title: z.string().min(5).max(200),
  /** Primary keyword the article should target. */
  targetKeyword: z.string().min(2).max(200),
  angle: z.string().max(500),
  searchIntent: z.enum(["informational", "commercial", "transactional", "navigational"]),
  rationale: z.string().max(600),
  questions: z.array(z.object({ group: questionGroupSchema, text: z.string().max(300) })).max(12).default([]),
  /**
   * Honesty flag: whether any real data backs this idea. "ai_suggested"
   * means the model proposed it with NO volume data — the UI must label it
   * so nobody mistakes it for measured search demand.
   */
  evidence: z.enum(["signal_backed", "ai_suggested"]),
  /** Index into the request's `signals` array when signal_backed. */
  signalIndexes: z.array(z.number().int().nonnegative()).max(20).default([]),
  /** Evidence the idea rests on, by reference ("E3"). */
  evidenceRefs: z.array(evidenceRefSchema).max(10).default([]),
});
export type TopicIdea = z.infer<typeof topicIdeaSchema>;

export const ideasResponseSchema = z.object({
  ideas: z.array(topicIdeaSchema).max(20),
  /** Ideas removed because they stated a figure that is in neither the signals nor the evidence. Counted by code, not by the model. */
  droppedForUnsupportedNumbers: z.number().int().nonnegative().default(0),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type IdeasResponse = z.infer<typeof ideasResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/draft — write an article draft (a proposal; the CMS saves nothing
// until a manager approves it)
// ---------------------------------------------------------------------------

export const draftRequestSchema = z.object({
  model: aiModelChoiceSchema.default({ provider: DEFAULT_AI_PROVIDER }),
  topic: z.object({
    title: z.string().min(5).max(200),
    targetKeyword: z.string().min(2).max(200),
    angle: z.string().max(500).optional(),
    questions: z.array(z.string().max(300)).max(12).default([]),
  }),
  audience: z.string().max(300).optional(),
  /** Only these sources may be cited. Empty = the draft must mark every factual claim as unverified. */
  sources: z.array(aiSourceSchema).max(12).default([]),
  /** Stored evidence the model may cite and take figures from. */
  evidence: z.array(evidenceItemSchema).max(40).default([]),
  /** Internal links the model may use: real existing articles (url + title). */
  internalLinks: z.array(z.object({ url: z.string(), title: z.string().max(300) })).max(40).default([]),
  memory: memoryPackSchema.default({ rules: [], examples: [], preferredTopics: [], avoidedTopics: [], profile: { voice: "", audience: "", avoid: "" }, knowledge: [] }),
  targetWords: z.number().int().min(300).max(4000).default(1500),
});
export type DraftRequest = z.infer<typeof draftRequestSchema>;

/** One checkable factual claim in the draft. */
export const draftClaimSchema = z.object({
  text: z.string().max(600),
  /** Index into the request's `sources` when the claim is backed by one; null = NOT backed. */
  sourceIndex: z.number().int().nonnegative().nullable(),
  /** Reference ("E3") of the stored evidence that backs the claim; null when none does. */
  evidenceRef: evidenceRefSchema.nullable().default(null),
  /** Reference ("K2") of the knowledge-library passage that backs the claim; null when none does. */
  knowledgeRef: knowledgeRefSchema.nullable().default(null),
  kind: z.enum(["statistic", "date", "name", "product_feature", "medical_or_legal", "other"]),
});
export type DraftClaim = z.infer<typeof draftClaimSchema>;

/** Where a figure in a draft fails the per-claim check (see draftClaimProblems). */
export const claimProblemSchema = z.object({
  /** wrong_record: the cited record does not contain the figure. no_backing: a figure with no record cited.
   *  not_listed: a figure in the text that no listed claim mentions, so no one is accountable for it. */
  reason: z.enum(["wrong_record", "no_backing", "not_listed"]),
  raw: z.string().max(80),
  context: z.string().max(240),
  /** Index into the draft's claims; null for not_listed. */
  claimIndex: z.number().int().nonnegative().nullable(),
});
export type ClaimProblem = z.infer<typeof claimProblemSchema>;

export const draftResponseSchema = z.object({
  title: z.string().min(5).max(200),
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(120),
  description: z.string().min(20).max(320),
  metaTitle: z.string().min(10).max(70),
  metaDescription: z.string().min(50).max(170),
  keywords: z.string().max(400),
  bodyMarkdown: z.string().min(200),
  faq: z.array(z.object({ question: z.string().max(300), answer: z.string().max(1200) })).max(10).default([]),
  /** Suggested alt-text-ready image briefs; the AI never generates or commits images. */
  imageBriefs: z.array(z.string().max(300)).max(6).default([]),
  claims: z.array(draftClaimSchema).max(80).default([]),
  /** Figures in the draft that match no evidence, source or brief. Computed by code (twice: AI Worker, then CMS), never by the model. */
  unsupportedNumbers: z.array(z.object({ raw: z.string().max(80), context: z.string().max(240) })).max(50).default([]),
  /** Figures attached to the wrong record, to none, or to no claim. Computed by the CMS in code, never by the model. */
  claimProblems: z.array(claimProblemSchema).max(50).default([]),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type DraftResponse = z.infer<typeof draftResponseSchema>;

// ---------------------------------------------------------------------------
// GET /v1/health — which providers are usable (key present), never the keys
// ---------------------------------------------------------------------------

export const aiHealthSchema = z.object({
  ok: z.boolean(),
  defaultProvider: aiProviderSchema,
  providers: z.array(z.object({ provider: aiProviderSchema, configured: z.boolean(), defaultModel: z.string().nullable() })),
});
export type AiHealth = z.infer<typeof aiHealthSchema>;

/** Error body the AI Worker returns on any non-2xx. */
export const aiErrorSchema = z.object({
  error: z.string(),
  code: z.enum([
    "UNAUTHORIZED",
    "BAD_REQUEST",
    "PROVIDER_NOT_CONFIGURED",
    "PROVIDER_ERROR",
    "OUTPUT_INVALID",
    "RATE_LIMITED",
    "INTERNAL_ERROR",
  ]),
});
export type AiError = z.infer<typeof aiErrorSchema>;

/**
 * Builds idea signals from the evidence pack: one signal per keyword, with its volume, impressions and clicks
 * taken from the evidence records. The reference points back to the first record used.
 */
export function evidenceToSignals(pack: readonly EvidenceItem[]): IdeaSignal[] {
  const bySubject = new Map<string, IdeaSignal>();
  for (const item of pack) {
    const value = item.numbers[0];
    if (value === undefined || !Number.isInteger(value) || value < 0) continue;
    const field = item.kind === "keyword.volume" ? "volume" : item.kind === "search.impressions" ? "impressions" : item.kind === "search.clicks" ? "clicks" : null;
    if (!field) continue;
    const key = item.subject.trim().toLowerCase();
    const existing = bySubject.get(key) ?? { query: item.subject.slice(0, 300), source: "evidence" as const, evidenceRef: item.ref };
    // The first (newest, most relevant) record for each field wins.
    if (existing[field] === undefined) existing[field] = value;
    bySubject.set(key, existing);
  }
  return [...bySubject.values()];
}

// ---------------------------------------------------------------------------
// The number check, written once. The AI Worker runs it on what the model returns; the CMS Worker runs it
// again on what the AI Worker returns. Neither trusts the model, and neither trusts the other.
// ---------------------------------------------------------------------------

/** Every figure an idea may mention: the evidence, the signals, and what the person typed. */
/** Text a manager wrote into the brain (the profile and product-fact / brand-document passages): figures in it are the brand's own, so they may be repeated. Past content and ads are for tone only and add no allowed figures. */
export function brainTexts(memory: MemoryPack | undefined): string[] {
  if (!memory) return [];
  return [memory.profile.voice, memory.profile.audience, memory.profile.avoid, ...memory.knowledge.filter((k) => k.kind === "product_fact" || k.kind === "brand_doc").flatMap((k) => [k.title, k.excerpt])];
}

export function ideaAllowedNumbers(req: Pick<IdeasRequest, "evidence" | "signals" | "seed" | "audience"> & { memory?: MemoryPack }): number[] {
  return allowedNumbers({
    pack: req.evidence,
    texts: [req.seed, req.audience ?? "", ...brainTexts(req.memory)],
    numbers: req.signals.flatMap((s) => [s.impressions, s.clicks, s.volume].filter((n): n is number => n !== undefined)),
  });
}

export function ideaUnsupportedNumbers(
  idea: Pick<TopicIdea, "title" | "targetKeyword" | "angle" | "rationale" | "questions">,
  allowed: readonly number[],
): UnsupportedNumber[] {
  const text = [idea.title, idea.targetKeyword, idea.angle, idea.rationale, ...idea.questions.map((q) => q.text)].join("\n");
  return findUnsupportedNumbers(text, allowed);
}

/** Every figure a draft may mention: the evidence, the supplied sources and the brief. */
export function draftAllowedNumbers(req: Pick<DraftRequest, "evidence" | "sources" | "topic" | "audience" | "internalLinks"> & { memory?: MemoryPack }): number[] {
  return allowedNumbers({
    pack: req.evidence,
    texts: [
      ...req.sources.flatMap((s) => [s.title, s.excerpt]),
      req.topic.title,
      req.topic.targetKeyword,
      req.topic.angle ?? "",
      ...req.topic.questions,
      req.audience ?? "",
      ...req.internalLinks.map((l) => l.title),
      ...brainTexts(req.memory),
    ],
  });
}

export function draftUnsupportedNumbers(
  draft: Pick<DraftResponse, "title" | "description" | "metaTitle" | "metaDescription" | "bodyMarkdown" | "faq">,
  allowed: readonly number[],
): UnsupportedNumber[] {
  const text = [draft.title, draft.description, draft.metaTitle, draft.metaDescription, draft.bodyMarkdown, ...draft.faq.flatMap((f) => [f.question, f.answer])].join("\n");
  return findUnsupportedNumbers(text, allowed).slice(0, 50);
}


// ---------------------------------------------------------------------------
// The per-claim check. The global number check asks "is this figure anywhere in the evidence?". That lets a real
// figure sit on the wrong claim (a click count quoted as a conversion rate). This asks the stricter question:
// "is the figure in the specific record the claim says backs it?" Code decides; the model's word is not used.
// ---------------------------------------------------------------------------


export const CLAIM_PROBLEM_TEXT: Record<ClaimProblem["reason"], string> = {
  wrong_record: "The figure is not in the record this claim points to.",
  no_backing: "A figure with no record behind it.",
  not_listed: "A figure in the text that no claim accounts for.",
};

export function draftClaimProblems(
  draft: Pick<DraftResponse, "title" | "description" | "metaTitle" | "metaDescription" | "bodyMarkdown" | "faq" | "claims">,
  ctx: {
    evidence: readonly { ref: string; numbers: readonly number[] }[];
    sources: readonly { title: string; excerpt: string }[];
    knowledge: readonly { ref: string; title: string; excerpt: string }[];
    /** Text the person typed (topic, audience, brand profile): their own figures need no claim. */
    briefNumbers: readonly number[];
  },
): ClaimProblem[] {
  const out: ClaimProblem[] = [];
  const evidenceBy = new Map(ctx.evidence.map((e) => [e.ref, e.numbers]));
  const knowledgeBy = new Map(ctx.knowledge.map((k) => [k.ref, extractAllNumbers(`${k.title}\n${k.excerpt}`)]));
  const claimed: number[] = [];

  draft.claims.forEach((claim, claimIndex) => {
    const figures = extractNumbers(claim.text).filter(isFigure);
    if (figures.length === 0) return;
    const backers: (readonly number[])[] = [];
    if (claim.evidenceRef) backers.push(evidenceBy.get(claim.evidenceRef) ?? []);
    if (claim.sourceIndex !== null) {
      const s = ctx.sources[claim.sourceIndex];
      backers.push(s ? extractAllNumbers(`${s.title}\n${s.excerpt}`) : []);
    }
    if (claim.knowledgeRef) backers.push(knowledgeBy.get(claim.knowledgeRef) ?? []);
    for (const f of figures) {
      claimed.push(f.value);
      const context = claim.text.slice(0, 240);
      if (backers.length === 0) {
        if (!ctx.briefNumbers.some((n) => n === f.value)) out.push({ reason: "no_backing", raw: f.raw, context, claimIndex });
      } else if (!backers.some((nums) => nums.some((n) => n === f.value))) {
        out.push({ reason: "wrong_record", raw: f.raw, context, claimIndex });
      }
    }
  });

  const text = [draft.title, draft.description, draft.metaTitle, draft.metaDescription, draft.bodyMarkdown, ...draft.faq.flatMap((f) => [f.question, f.answer])].join("\n");
  const seen = new Set<string>();
  for (const n of extractNumbers(text)) {
    if (!isFigure(n) || claimed.includes(n.value) || ctx.briefNumbers.includes(n.value)) continue;
    const key = n.raw.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const prose = stripNonProse(text);
    const from = Math.max(0, n.index - 30);
    out.push({ reason: "not_listed", raw: n.raw, context: prose.slice(from, n.index + n.raw.length + 30).replace(/\s+/g, " ").trim(), claimIndex: null });
  }
  return out.slice(0, 50);
}

/** The figures the person typed themselves (topic, audience, brand profile and fact sheet): the brand's own, so no claim is needed. */
export function draftBriefNumbers(req: Pick<DraftRequest, "topic" | "audience" | "internalLinks"> & { memory?: MemoryPack }): number[] {
  return allowedNumbers({
    texts: [req.topic.title, req.topic.targetKeyword, req.topic.angle ?? "", ...req.topic.questions, req.audience ?? "", ...req.internalLinks.map((l) => l.title), ...brainTexts(req.memory)],
  });
}
